import React, { useContext, useMemo, useRef, useState } from 'react';
import classes from './FirstComment.module.scss';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { CSSTransition } from 'react-transition-group';
import { useDidMount } from '../../../hooks/useDidMount';
import { IconsEnum } from '../../../constants/icons';
import { CustomParagraph } from '../../../models/slate-editor';
import { createFirstCommentEditor } from '../TextEditor/helpers/createEditorWithPlugins';
import { createEditor } from 'slate';
import { FirstCommentEditor } from '../FirstCommentEditor';
import { SlateToolbar } from '../TextEditor/SlateToolbar';
import { idGenerator } from '../../../helpers/idGenerator';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { PostEditorContext } from '../states/PostEditorProvider';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from 'constants/form';
import { getErrorMessages } from '../validators/helpers/getErrorMessages';
import { formatErrorMessages } from '../validators/helpers/formatErrorMessages';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';

export type FirstCommentViewProps = {
  //
  onRemove: VoidFunction;
  firstComment: CustomParagraph[];
  hashtagsCount?: number;
};

export const FirstCommentView = (props: FirstCommentViewProps): JSX.Element => {
  const { onRemove, firstComment, hashtagsCount } = props;
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const [editor] = useState(() => createFirstCommentEditor(createEditor()));
  const { state } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  useDidMount(() => {
    // we need to setMount to true, since we want to trigger animation (fade) only when the component has been completely mounted.
    setMount(true);
    return () => {
      setMount(false);
    };
  });

  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts } = postGroup;
  const isCustomizingByProfile = customizeContentPerProfile && typeof customizeIndex === 'number';
  const profileId = isCustomizingByProfile ? posts[customizeIndex].customizedProfileId : null;

  const id = useMemo(() => idGenerator('firstComment'), []);
  const { errors } = postGroup;

  const hasError = hasSeverity(errors, ErrorMessageSeverity.error, profileId, null, [
    'instagramFacebookFirstCommentCharLimit',
    'linkedinFirstCommentCharLimit'
  ]);
  const errorMessages = getErrorMessages(errors, profileId, null, [
    'instagramFacebookFirstCommentCharLimit',
    'linkedinFirstCommentCharLimit'
  ]);
  const hasHashtagLimitError = hasSeverity(errors, ErrorMessageSeverity.error, profileId, undefined, [
    'instagramHashtagLimit'
  ]);

  const hashtagLimitErrorMessages = getErrorMessages(errors, profileId, undefined, ['instagramHashtagLimit']);

  const comboErrorMessages: string[] = [
    ...(hasError ? errorMessages : []),
    ...(hasHashtagLimitError ? hashtagLimitErrorMessages : [])
  ];

  const boxClasses = classNames(classes.FirstComment, {
    [classes.FirstCommentError]: hasError || hasHashtagLimitError,
    [POST_EDITOR_ERROR_CLASS]: hasError
  });

  return (
    <CSSTransition in={mount} timeout={300} classNames={'fade'} nodeRef={cssRef} unmountOnExit>
      <div className={boxClasses} ref={cssRef}>
        <div className={'grid-x align-middle, align-justify'}>
          <div className={'cell shrink'}>
            <div className={'grid-x align-middle'}>
              <div className={classNames(classes.FirstCommentLabel, 'cell', 'shrink')}>First Comment</div>
              <div className={'cell shrink'}>
                <Tooltip
                  target={`#${id}profile${profileId}`}
                  position={'right'}
                  pointer
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}
                  {...(comboErrorMessages.length ? { icon: 'alert' } : {})}>
                  <>
                    {comboErrorMessages.length
                      ? formatErrorMessages(comboErrorMessages)
                      : 'Auto-post the first comment. Only used for Facebook Page posts, LinkedIn posts, or Instagram posts that will be direct-published.'}
                  </>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className={classNames(classes.RemoveComment, 'cell shrink')}>
            <Tooltip
              target={`#${id}tooltipDeleteFirstComment${profileId}`}
              position={'right'}
              pointer
              type={'regular'}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              Remove first comment
            </Tooltip>
            <div id={`${id}tooltipDeleteFirstComment${profileId}`} className={'cell shrink'}>
              <Button
                action={() => onRemove()}
                size={'medium'}
                color={ButtonColorsEnum.primary}
                type={'button'}
                icon={{
                  side: 'center',
                  value: IconsEnum.trash
                }}
                variant={'clear'}
              />
            </div>
          </div>
        </div>
        <div className={'grid-x margin-top-small-16'}>
          <div className={classes.Editor}>
            <FirstCommentEditor editor={editor} firstComment={firstComment} />
            <SlateToolbar
              emojiTooltip={'Add emojis'}
              snippetsTooltip={'Add Snippets to your First Comment, with any text that you re-use frequently.'}
              canDelete={false}
              comboErrorMessages={comboErrorMessages}
              // errorMessages={hasHashtagLimitError ? hashtagLimitErrorMessages : []}
              comboWarningMessages={[]}
              errorVariationMessages={[]}
              warningVariationMessages={[]}
              {...(typeof hashtagsCount === 'number' ? { hashtagsCount } : {})}
              editor={editor}
              publishNext={false}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};
